import moment, { Moment } from 'moment-timezone'
import { TIME_FORMAT, TIME_INPUT_FORMAT } from '../types'

export const dateWithSelectedTime = (
  dateString: string,
  timeString: string,
  timezone: string
): string => {
  const time = moment(timeString, TIME_FORMAT)
  return moment
    .parseZone(dateString)
    .set({
      hour: time.get('hour'),
      minute: time.get('minute'),
      second: 0,
    })
    .tz(timezone, true)
    .format()
}

/**
 * Validates a time based on whether the numbers are within a range
 * for selected timeFormat
 */
export const isValidTime = (
  timeFormat: TIME_INPUT_FORMAT,
  hours: string,
  minutes?: string
): boolean => {
  const maxHourValue = timeFormat === TIME_INPUT_FORMAT.MILITARY_SHORT ? 12 : 23
  const minHourValue = timeFormat === TIME_INPUT_FORMAT.MILITARY_SHORT ? 1 : 0
  const hoursNumber = parseInt(hours, 10)
  const minutesNumber = parseInt(minutes || '0', 10)
  return hoursNumber >= minHourValue && hoursNumber <= maxHourValue && minutesNumber <= 59
}

/**
 * Returns a formatted string, i.e. '10:30' or '04:21'
 */
export const formatTime = (
  timeFormat: TIME_INPUT_FORMAT,
  hours: string,
  minutes: string = '0'
): string => {
  const maxHourValue = timeFormat === TIME_INPUT_FORMAT.MILITARY_SHORT ? 12 : 23
  const hourValue = parseInt(hours || `${maxHourValue}`, 10)

  const militaryHour = hourValue < 10 ? `0${hourValue}` : hourValue
  const hourFormatted = timeFormat === TIME_INPUT_FORMAT.MILITARY_SHORT ? hourValue : militaryHour
  const minutesNumber = parseInt(minutes || '0', 10)

  return `${hourFormatted}:${minutesNumber > 9 ? '' : '0'}${minutesNumber}`
}

/**
 * Returns a string that parses the time value and removes/adds to create
 * valid time string
 * @param {string} input - raw time input
 * @param {TIME_INPUT_FORMAT} timeFormat - military or non-military format
 */
export const parseTime = (
  input: string = '',
  timeFormat: TIME_INPUT_FORMAT
): string | undefined => {
  const normalizedInput = input.toLowerCase().replace(/\s/g, '')
  let parsedTime = ''

  const [numbers] = /^\d+:?(\d+)?/.exec(normalizedInput) || ['']

  if (numbers.includes(':')) {
    // hours and minutes with colon between
    const [h, m] = numbers.split(':')
    if (isValidTime(timeFormat, h, m)) parsedTime = formatTime(timeFormat, h, m)
  } else if (numbers.length < 3) {
    // just hours
    if (isValidTime(timeFormat, numbers)) parsedTime = formatTime(timeFormat, numbers)
  } else if (numbers.length === 3 && isValidTime(timeFormat, numbers[0], numbers.slice(1, 3))) {
    // 1 digit for hours and 2 for minutes without delimiter
    parsedTime = formatTime(timeFormat, numbers[0], numbers.slice(1, 3))
  } else if (isValidTime(timeFormat, numbers.slice(0, 2), numbers.slice(2, 4))) {
    // 2 digits for hours and 1 or 2 for minutes without delimiter
    parsedTime = formatTime(timeFormat, numbers.slice(0, 2), numbers.slice(2, 4))
  }

  if (!parsedTime) return undefined

  if (timeFormat === TIME_INPUT_FORMAT.MILITARY) return parsedTime

  const [letters] = /[a-z]+$/.exec(normalizedInput) || []
  const isAM = ['a', 'am'].includes(letters)
  const isPM = ['p', 'pm'].includes(letters)
  if (letters && !isAM && !isPM) return undefined

  return isAM ? `${parsedTime}am` : `${parsedTime}pm`
}

export const MINUTES_PER_DAY = 24 * 60
export const DEFAULT_SUGGESTION_STEP_MINUTES = 30

/**
 * Returns array of times in string format
 * @param {Moment} startAt - Moment when the times begin
 * @param {TIME_INPUT_FORMAT} timeFormat - i.e. 'MILITARY' or 'MILITARY_SHORT'
 * @param {number} suggestionCount - How many results to show
 * @param {number} minuteIncrement - Increments, i.e. '30', '15'
 * @returns i.e. ['10:00am', '10:30am']
 */
export const buildTimes = (
  startAt: Moment,
  timeFormat: TIME_INPUT_FORMAT,
  minuteIncrement: number = DEFAULT_SUGGESTION_STEP_MINUTES
): string[] => {
  const suggestionCount = MINUTES_PER_DAY / minuteIncrement // Default: 48
  return Array.from({ length: suggestionCount }, (n, i) =>
    startAt
      .clone()
      .add(minuteIncrement * i, 'm')
      .format(timeFormat)
  )
}

/**
 * Returns a Moment object with specific date/time
 * @param {string} date - i.e. '10/20/2020'
 * @param {string} time - i.e. '10:00am'
 * @param {string} timeFormat - i.e. 'mm/dd/yyyy'
 * @param {string} timezone - i.e. 'America/New_York'
 */
export const buildDateTime = (
  date?: string,
  time?: string,
  timeFormat?: string,
  timezone?: string
): Moment | undefined => {
  if (!date || !time) return undefined
  const dateMoment = moment(date).startOf('d')
  const timeMoment = moment(time, timeFormat)
  const dateWithNewTime = dateMoment
    .set({
      hours: timeMoment.hours(),
      minutes: timeMoment.minutes(),
    })
    .tz(timezone ?? moment.tz.guess(), true)
  return dateWithNewTime
}

export const floor = (value: number) => {
  return (value < 0 ? -1 : 1) * Math.floor(Math.abs(value))
}

export interface IGetSuggestionsArg {
  guessedOption?: string
  anchorDateTime?: Moment
  timeFormat: TIME_INPUT_FORMAT
  minuteIncrement: number
}

/**
 * Get list of suggestions for <TimeInput />
 */
export const getSuggestions = ({
  guessedOption,
  anchorDateTime = moment().startOf('d').startOf('hour'),
  timeFormat,
  minuteIncrement,
}: IGetSuggestionsArg): string[] => {
  if (guessedOption || anchorDateTime) {
    const time =
      !!guessedOption && !!timeFormat && moment(guessedOption, timeFormat).isValid()
        ? moment(guessedOption, timeFormat)
        : anchorDateTime
    return buildTimes(
      time
        .clone()
        .startOf('d')
        .minutes(60 % minuteIncrement),
      timeFormat,
      minuteIncrement
    )
  }
  // Default options
  return buildTimes(moment().startOf('day').startOf('hour'), timeFormat, minuteIncrement)
}
